import type {
  ApiDashboardElement,
  Collection,
  ReportStructure,
  TextFromEntityPropertyJSON,
} from '@highimpact-frontend/api-types';
import type { PlotTypes } from '@highimpact/dashboard-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useDashboardLibraryDispatch,
  useDashboardLibrarySelector,
} from '../runtime/DashboardLibraryProvider';
import { hydrateDashboardLibraryState } from '../state/actions';
import {
  finishRemoteWidgetConfiguration,
  getPlotType,
} from '../state/dashboard-element-operations';
import {
  setWidgetConfigPanelState,
  setWidgetFormIsOpen,
} from '../state/dashboard/slice';

import {
  GenericErrorBoundary,
  RightSideBarify,
} from '@highimpact/dashboard-ui';
import ErrorIcon from '@mui/icons-material/Error';
import { Typography } from '@mui/material';
import {
  useDashboardHost,
  useDashboardScope,
} from '../context/DashboardHostContext';
import UnhandledForm from '../dashboard/widget/commons/forms/UnHandledForm';
import UnhandledPlotForm from '../dashboard/widget/commons/forms/UnhandledPlotForm';
import AggregationTableForm from '../dashboard/widget/widgets/aggregationTable/AggregationTableForm';
import HistogramPlotForm from '../dashboard/widget/widgets/histogramPlot/HistogramPlotForm';
import ImageForm from '../dashboard/widget/widgets/image/ImageForm';
import ImageGalleryForm from '../dashboard/widget/widgets/imageGallery/ImageGalleryForm';
import MapForm from '../dashboard/widget/widgets/map/MapForm';
import OmbrothermicPlotForm from '../dashboard/widget/widgets/ombrothermicPlot/OmbrothermicPlotForm';
import RemoteWidgetForm from '../dashboard/widget/widgets/remoteWidget/RemoteWidgetForm';
import ScatterForm from '../dashboard/widget/widgets/scatterPlot/ScatterPlotForm';
import TableByCampaignForm from '../dashboard/widget/widgets/tableByCampaign/TableByCampaignForm';
import TextForm from '../dashboard/widget/widgets/text/TextForm';
import TextFromEntityPropertyForm from '../dashboard/widget/widgets/textFromProperty/TextFromProperty';
import TimeSeriesForm from '../dashboard/widget/widgets/timeSeries/TimeSeriesForm';
import WindRosePlotForm from '../dashboard/widget/widgets/windRosePlot/WindRosePlotForm';
import useMFEMetaData, {
  MFEMetaDataState,
} from '../internal/hooks/useMFEMetaData';
import * as R from '../internal/utils/record';
import { WidgetType } from '../state/dashboard/widget';
import { buildMapping } from '../state/report-structure-operations';
import {
  addWidgetSafely,
  getWidgetFromIdMapping,
  removeWidgetSafely,
  ReportDashboardDependencies,
} from '../state/store-operations';
import {
  cloneDashboardElementForForm,
  fetchSourceLabelMapForElement,
  pickMigratedFormProps,
  useCampaignsForSite,
} from './formHandlerShared';
import { fullPathElementTypes } from './initiatorCatalogSeeding';

export type { PlotFormProps } from '../dashboard/widget/widgets/shared/types';

export {
  fullPathElementTypes,
  partialPathElementTypes,
} from './initiatorCatalogSeeding';

export const plotTypes: PlotTypes[] = [
  'timeseries',
  //'scatter',
  'windrose',
  'bar',
  'ombrothermic',
];

// -- Pretty names for the selectors in all forms

export const getElementPrettyName = (
  element_type: Exclude<
    ApiDashboardElement['element_type'],
    'collection' | 'readonlytext'
  >
): string => {
  switch (element_type) {
    case 'plot':
      return 'Graphique';
    case 'map':
      return 'Carte';
    case 'table':
      return 'Tableau de données';
    case 'tablebycampaign':
      return 'Tableau de campagnes';
    case 'text':
      return 'Zone de texte';
    case 'localizationfromcoordinates':
      return "Coordonnées d'une ressource";
    case 'textfromentityproperty':
      return "Propriété d'une ressource";
    case 'image':
      return 'Image';
    case 'remotewidget':
      return 'Plugins';
    case 'imagegallery':
      return "Galerie d'images";
  }
};

// -- Element type & pretty name mapping
export const elementTypeToPrettyName: Record<
  Exclude<ApiDashboardElement['element_type'], 'collection' | 'readonlytext'>,
  {
    label: string;
    formTitle: string;
    specificWidgetType?: string;
  }
> = {
  plot: { label: 'Graphique', formTitle: 'Ajouter un graphique' },
  map: {
    label: 'Carte',
    formTitle: 'Ajouter une carte',
    specificWidgetType: 'map-editor-react',
  },
  table: {
    label: 'Tableau de données',
    formTitle: 'Ajouter un tableau de données',
  },
  tablebycampaign: {
    label: 'Tableau de campagnes',
    formTitle: 'Ajouter un tableau de campagnes',
  },
  text: { label: 'Zone de texte', formTitle: 'Ajouter une zone de texte' },
  localizationfromcoordinates: {
    label: "Coordonnées d'une ressource",
    formTitle: "Ajouter des coordonnées d'une ressource",
  },
  textfromentityproperty: {
    label: "Propriété d'une ressource",
    formTitle: "Ajouter une propriété d'une ressource",
  },
  image: { label: 'Image', formTitle: 'Ajouter une image' },
  remotewidget: { label: 'Plugin', formTitle: 'Ajouter un plugin' },
  imagegallery: {
    label: "Galerie d'images",
    formTitle: "Ajouter une galerie d'images",
  },
};

// -- Pretty names for the selectors in the plot form

export const getPlotTypePrettyName = (plot_type: PlotTypes): string => {
  switch (plot_type) {
    case 'timeseries':
      return 'Séries temporelles';
    case 'scatter':
      return 'Nuage de points';
    case 'bar':
      return 'Histogramme';
    case 'pie':
      return 'Camembert';
    case 'heatmap':
      return 'Carte thermique';
    case 'windrose':
      return 'Rose des vents';
    case 'geo':
      return 'Carte géographique';
    case 'ombrothermic':
      return 'Ombrothermie';
  }
};

// -- Pretty names for the selectors in the textfromentityproperty form

export const getResourcePrettyName = (
  entity_type: TextFromEntityPropertyJSON['entity_type']
): string => {
  switch (entity_type) {
    case 'project':
      return 'Projet';
    case 'site':
      return 'Site';
  }
};

const getProjectKeyPrettyName = (key: 'name'): string => {
  switch (key) {
    case 'name':
      return 'Nom';
  }
};

const getSiteKeyPrettyName = (key: 'name'): string => {
  switch (key) {
    case 'name':
      return 'Nom';
  }
};

export const getPropertyPrettyName = <
  T extends TextFromEntityPropertyJSON['entity_type']
>(
  entity_type: T,
  entity_property: string
): string => {
  switch (entity_type) {
    case 'project':
      return getProjectKeyPrettyName(entity_property as 'name');
    case 'site':
      return getSiteKeyPrettyName(entity_property as 'name');
    default:
      return entity_property;
  }
};

// -- Forms

interface ConfigWidgetFormHandlerProps {
  gridType: 'grids' | 'reports';
  mode: 'configuring' | 'reconfiguring';
  widgetId: number;
  grid_id: number;
  configuration: ApiDashboardElement;
  widget: WidgetType;
  mfeMetaData: MFEMetaDataState;
}

interface ConfigWidgetFormHandlerHelperProps {
  gridType: 'grids' | 'reports';
  mode: 'configuring' | 'reconfiguring';
  widgetId: number;
  grid_id: number;
}

function ConfigWidgetFormHandler({
  gridType,
  mode,
  widget,
  configuration,
  mfeMetaData,
  ...props
}: ConfigWidgetFormHandlerProps) {
  const scope = useDashboardScope();
  const host = useDashboardHost();
  const site_id = scope.site_id;
  const dashboard_id = props.grid_id ?? scope.grid_id;
  const dispatch = useDashboardLibraryDispatch();
  const campaignsForSite = useCampaignsForSite(site_id);
  const [config, setConfig] = useState<Exclude<
    ApiDashboardElement,
    Collection
  > | null>(null);

  // default date values: from today at midnight to tomorrow at midnight
  const startDate = new Date();
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 1);

  const dashboardData = useDashboardLibrarySelector((state) =>
    dashboard_id ? state.dashboard[gridType][dashboard_id] : undefined
  );

  const store = useDashboardLibrarySelector((state) => state);

  const configFormOpen = useDashboardLibrarySelector(
    (state) => state.dashboard.ConfigWidgetPanelState
  );

  useEffect(() => {
    if (configFormOpen.state === 'NotConfiguring') {
      setConfig(null);
    }
  }, [configFormOpen.state]);

  const onChange = useCallback(
    (nextConfig: Exclude<ApiDashboardElement, Collection>) => {
      setConfig(cloneDashboardElementForForm(nextConfig));
    },
    []
  );

  const editableConfig = useMemo(
    () => cloneDashboardElementForForm(config ?? widget.elementData),
    [config, widget.elementData]
  );

  if (configFormOpen.state === 'NotConfiguring') {
    return null;
  }

  if (mfeMetaData.status === 'init' || mfeMetaData.status === 'loading') {
    return (
      <div>
        <p>Chargement des plugins...</p>
      </div>
    );
  }

  if (mfeMetaData.status === 'error') {
    return (
      <div>
        <p>Récupération impossible des plugins</p>
      </div>
    );
  }

  const project_id = host.project_id ?? host.project?.id ?? null;

  if (!dashboard_id) {
    return (
      <div>
        <p>L'identifiant du tableau de bord n'a pas pu être récupéré</p>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div>
        <p>Les données du tableau de bord n'ont pas pu être récupérées</p>
      </div>
    );
  }

  const elementType = widget['elementData']['element_type'] as Exclude<
    ApiDashboardElement['element_type'],
    'collection' | 'readonlytext'
  >;
  const elementTypes = fullPathElementTypes;

  const baseDep = {
    startDate,
    endDate,
    w: 12,
    h: 9,
  };

  const reportStructure = dashboardData.params as ReportStructure | undefined;
  const reportDependencies: ReportDashboardDependencies | undefined =
    reportStructure && gridType === 'reports'
      ? {
          reportStructure,
          layouts: [],
          elementNameToVariableName: R.swapKeysAndValues(
            buildMapping(reportStructure)
          ),
        }
      : undefined;

  const onSubmit = async (config: Exclude<ApiDashboardElement, Collection>) => {
    const dashboardElementId = widget.dashboardElementId;
    if (dashboardElementId == null) {
      return;
    }

    const configToSubmit = (
      elementType === 'remotewidget'
        ? finishRemoteWidgetConfiguration(config)
        : config
    ) as Exclude<ApiDashboardElement, Collection>;
    try {
      await host.api.dashboard.putDashboardElement(
        dashboardElementId,
        configToSubmit
      );

      let newState = removeWidgetSafely(dashboardElementId, gridType, store);

      const sourceLabelMap = await fetchSourceLabelMapForElement(
        configToSubmit,
        host.api.sources.getLabels,
        {
          project_id: project_id ?? undefined,
          site_id: site_id ?? undefined,
        }
      );
      const dep = {
        ...baseDep,
        sourceLabelMap,
      };

      newState =
        gridType === 'reports'
          ? addWidgetSafely(
              {
                ...dep,
                ...(reportDependencies as ReportDashboardDependencies),
                posistioningStrategy: 'default',
              },
              dashboard_id,
              gridType,
              { ...configToSubmit, id: dashboardElementId },
              newState
            )
          : addWidgetSafely(
              { ...dep, posistioningStrategy: 'default' },
              dashboard_id,
              gridType,
              { ...configToSubmit, id: dashboardElementId },
              newState
            );
      dispatch(hydrateDashboardLibraryState(newState));
      dispatch(
        setWidgetConfigPanelState({
          state: 'NotConfiguring',
        })
      );
    } catch {
      dispatch(setWidgetFormIsOpen({ state: 'closed' }));
    }
  };
  const formProps = {
    mfeMetaData: mfeMetaData.data,
    config: editableConfig,
    project_id,
    site_id: site_id,
    campaigns: campaignsForSite,
    start_time: startDate,
    end_time: endDate,
    elementTypes,
    onChange: onChange as (config: ApiDashboardElement) => void,
    onSubmit: onSubmit as (config: ApiDashboardElement) => Promise<void | {
      payload: boolean;
      type: 'dashboard-slice/setWidgetFormIsOpen';
    }>,
  };
  const migratedFormProps = pickMigratedFormProps(formProps);

  let formContent: JSX.Element;
  switch (elementType) {
    case 'plot':
      switch (getPlotType(formProps.config)) {
        case 'timeseries':
          formContent = <TimeSeriesForm {...migratedFormProps} />;
          break;
        case 'bar':
          formContent = <HistogramPlotForm {...migratedFormProps} />;
          break;
        case 'scatter':
          formContent = <ScatterForm {...migratedFormProps} />;
          break;
        case 'windrose':
          formContent = <WindRosePlotForm {...migratedFormProps} />;
          break;
        case 'ombrothermic':
          formContent = <OmbrothermicPlotForm {...migratedFormProps} />;
          break;
        default:
          formContent = <UnhandledPlotForm {...migratedFormProps} />;
      }
      break;
    case 'map':
      formContent = <MapForm {...migratedFormProps} />;
      break;
    case 'table':
      formContent = (
        <AggregationTableForm
          {...migratedFormProps}
          campaigns={campaignsForSite}
        />
      );
      break;
    case 'tablebycampaign':
      formContent = <TableByCampaignForm {...migratedFormProps} />;
      break;
    case 'text':
      formContent = <TextForm {...migratedFormProps} />;
      break;
    case 'localizationfromcoordinates':
      formContent = <UnhandledForm />;
      break;
    case 'textfromentityproperty':
      formContent = <TextFromEntityPropertyForm {...migratedFormProps} />;
      break;
    case 'image':
      formContent = <ImageForm {...migratedFormProps} />;
      break;
    case 'remotewidget':
      formContent = <RemoteWidgetForm {...migratedFormProps} />;
      break;
    case 'imagegallery':
      formContent = <ImageGalleryForm {...migratedFormProps} />;
      break;
    default:
      formContent = <UnhandledForm />;
  }

  return (
    <GenericErrorBoundary
      resetKey={`${props.widgetId}:${configFormOpen.state}`}
      fallback={
        <Typography component="h4" align="center">
          <ErrorIcon /> Impossible d&apos;afficher le formulaire de
          configuration
        </Typography>
      }
    >
      {formContent}
    </GenericErrorBoundary>
  );
}

function ConfigWidgetFormHandlerHelper(
  props: ConfigWidgetFormHandlerHelperProps
) {
  const host = useDashboardHost();
  const mfeMetaData = useMFEMetaData();

  const { widgetId, gridType, grid_id: gridId, mode } = props;

  const widget = useDashboardLibrarySelector((state) =>
    getWidgetFromIdMapping(
      widgetId.toString(),
      gridType,
      gridId,
      state.dashboard
    )
  );

  const [configuration, setConfiguration] =
    useState<ApiDashboardElement | null>(null);

  useEffect(() => {
    if (!widget?.dashboardElementId) return;
    host.api.dashboard
      .getDashboardElement(widget.dashboardElementId)
      .then((value) => setConfiguration(value))
      .catch(console.error);
  }, [host.api.dashboard, widget?.dashboardElementId]);

  if (!configuration || !widget) return null;

  return (
    <div className="config-widget-form-handler-root">
      <ConfigWidgetFormHandler
        widgetId={widgetId}
        mode={mode}
        grid_id={gridId}
        gridType={gridType}
        configuration={configuration}
        widget={widget}
        mfeMetaData={mfeMetaData}
      />
    </div>
  );
}

export default RightSideBarify<ConfigWidgetFormHandlerHelperProps>(
  ConfigWidgetFormHandlerHelper
);
